import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tag, Clock, ShoppingCart } from 'lucide-react';
import { coupons as couponsApi, type CouponAPI } from '../api/client';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';

const PRODUCT_IMAGES = [
  '/uploads/hq/zyn/zyn-cool-mint-11mg.png',
  '/uploads/hq/velo/velo-freezing-peppermint-17mg.png',
  '/uploads/hq/pablo/pablo-grape-ice-17mg.png',
  '/uploads/hq/whitefox/whitefox-peppered-mint.png',
  '/uploads/hq/velo/velo-icy-berries-10mg.png',
  '/uploads/hq/zyn/zyn-citrus-11mg.png',
  '/uploads/hq/velo/velo-mango-ice-10mg.png',
  '/uploads/hq/zyn/zyn-spearmint-11mg.png',
  '/uploads/hq/pablo/pablo-frosted-mint-17mg.png',
  '/uploads/hq/velo/velo-bright-spearmint-6mg.png',
  '/uploads/hq/whitefox/whitefox-full-charge.png',
  '/uploads/hq/zyn/zyn-blackcurrant-frost-13mg.png',
];

const GRADIENTS = [
  'from-emerald-600 via-emerald-700 to-teal-800',
  'from-teal-600 via-cyan-700 to-emerald-800',
  'from-purple-600 via-purple-700 to-indigo-800',
  'from-slate-700 via-slate-800 to-gray-900',
  'from-emerald-700 via-teal-800 to-cyan-900',
  'from-indigo-600 via-purple-700 to-emerald-800',
];

function getDiscountLabel(c: CouponAPI): string {
  if (c.discount_type === 'percentage') {
    const cap = c.max_discount ? ` up to \u20b9${c.max_discount.toLocaleString('en-IN')}` : '';
    return `${c.discount_value}% OFF${cap}`;
  }
  return `\u20b9${c.discount_value.toLocaleString('en-IN')} OFF`;
}

function getExpiryLabel(c: CouponAPI): string | null {
  if (!c.valid_until) return null;
  const d = new Date(c.valid_until);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Deterministic but varied: pick image/gradient based on index */
function pickImage(idx: number) {
  return PRODUCT_IMAGES[idx % PRODUCT_IMAGES.length];
}
function pickGradient(idx: number) {
  return GRADIENTS[idx % GRADIENTS.length];
}

const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
};

export default function CouponBanner() {
  const [activeCoupons, setActiveCoupons] = useState<CouponAPI[]>([]);
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const { totalPrice } = useCart();
  const { isDark } = useTheme();

  useEffect(() => {
    couponsApi.active().then(setActiveCoupons).catch(() => {});
  }, []);

  const count = activeCoupons.length;

  const goTo = useCallback((idx: number, dir: number) => {
    setDirection(dir);
    setCurrent(idx);
  }, []);

  const next = useCallback(() => {
    if (count <= 1) return;
    goTo((current + 1) % count, 1);
  }, [current, count, goTo]);

  const prev = useCallback(() => {
    if (count <= 1) return;
    goTo((current - 1 + count) % count, -1);
  }, [current, count, goTo]);

  // Touch swipe support
  const touchStartX = useRef(0);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      if (delta > 0) next();
      else prev();
    }
  }, [next, prev]);

  // Auto-slide for multiple coupons
  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [count, next]);

  if (count === 0) return null;

  const coupon = activeCoupons[current];
  const eligible = !coupon.min_order_value || totalPrice >= coupon.min_order_value;
  const amountNeeded = coupon.min_order_value ? coupon.min_order_value - totalPrice : 0;

  return (
    <div className="mb-6">
      <div className="relative overflow-hidden rounded-2xl" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={coupon.id}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className={`bg-gradient-to-r ${pickGradient(current)} rounded-2xl relative`}
          >
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />

            <div className="relative flex flex-col sm:flex-row items-center gap-4 sm:gap-8 p-5 sm:p-8">
              {/* Text side */}
              <div className="flex-1 text-white text-center sm:text-left min-w-0">
                <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold mb-3">
                  <Tag size={12} />
                  LIMITED OFFER
                </div>
                <h3 className="text-2xl sm:text-3xl font-extrabold leading-tight mb-1">
                  {getDiscountLabel(coupon)}
                </h3>
                {coupon.description && (
                  <p className="text-white/80 text-sm mb-2">{coupon.description}</p>
                )}
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 text-xs text-white/70 mb-3">
                  {coupon.min_order_value && (
                    <span className="flex items-center gap-1">
                      <ShoppingCart size={12} />
                      Min. order {'\u20b9'}{coupon.min_order_value.toLocaleString('en-IN')}
                    </span>
                  )}
                  {getExpiryLabel(coupon) && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      Expires {getExpiryLabel(coupon)}
                    </span>
                  )}
                </div>

                {/* Code + eligibility */}
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <code className="bg-white/20 backdrop-blur-sm text-white font-bold px-4 py-2 rounded-lg text-sm tracking-wider border border-white/20">
                    {coupon.code}
                  </code>
                  {!eligible && amountNeeded > 0 && (
                    <span className="text-xs text-amber-200 font-medium">
                      Add {'\u20b9'}{amountNeeded.toLocaleString('en-IN')} more to unlock
                    </span>
                  )}
                  {eligible && totalPrice > 0 && (
                    <span className="text-xs text-emerald-200 font-medium">
                      {'\u2713'} Eligible! Apply at checkout
                    </span>
                  )}
                </div>
              </div>

              {/* Image side */}
              <div className="flex-shrink-0 w-28 h-28 sm:w-36 sm:h-36 relative">
                <div className="absolute inset-0 bg-white/10 rounded-full blur-xl" />
                <img
                  src={pickImage(current)}
                  alt={`${getDiscountLabel(coupon)} on nicotine pouches at ZeroSmoke — coupon ${coupon.code}`}
                  className="relative w-full h-full object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dot indicators */}
      {count > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {activeCoupons.map((c, i) => (
            <button
              key={c.id}
              onClick={() => goTo(i, i > current ? 1 : -1)}
              className={`rounded-full transition-all duration-300 ${
                i === current
                  ? 'w-6 h-2 bg-emerald-500'
                  : `w-2 h-2 ${isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-300 hover:bg-gray-400'}`
              }`}
              aria-label={`Go to coupon ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
